import React, {ReactNode} from "react";
import {CertificateEntry} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {I18nGlobals} from "../translation/i18nInterface";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import i18next from "i18next";
import FileSizeUnit from "../units/FileSizeUnit";
import {Tree, TreeCheckboxSelectionKeys, TreeExpandedKeysType, TreeMultipleSelectionKeys} from "primereact/tree";
import TreeNode from "primereact/treenode";
import Translate from "../translation/Translate";
import FunctionHelper from "../helper/FunctionHelper";
import PortalPureComponent from "./PortalPureComponent";

interface KeystoreEntryState {
	selected?: string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null;
	expanded: TreeExpandedKeysType;
}

interface KeystoreEntryProperties extends I18nGlobals {
	certificate: CertificateEntry;
}

export class KeystoreEntry extends PortalPureComponent<KeystoreEntryProperties, KeystoreEntryState> {
	private readonly CERTIFICATE_START: string = "-----BEGIN CERTIFICATE-----";
	private readonly CERTIFICATE_END: string = "-----END CERTIFICATE-----";

	constructor(props: Readonly<KeystoreEntryProperties>) {
		super(props);

		this.state = {
			expanded: {}
		};
	}

	private buildCertificateChain(certificates: Array<x509.X509Certificate>): Array<TreeNode> {
		let childs: Array<x509.X509Certificate> = [...certificates];
		let certificate: x509.X509Certificate | undefined = childs.pop();

		if (!FunctionHelper.exists(certificate)) {
			return [];
		}

		return [
			{
				key: certificate?.subject,
				label: certificate?.subject,
				selectable: true,
				leaf: childs.length === 0,
				data: certificate,
				children: this.buildCertificateChain(childs)
			}
		];
	}

	private buildCertificateValues(certificate: x509.X509Certificate): Array<{
		label: ReactNode, value: ReactNode, className?: string
	}> {
		let currentDate: Date = new Date();

		return [
			{
				label: <Translate {...this.props} data="control.keystore_viewer.subject"/>,
				value:
					<span className="tooltip" data-pr-tooltip={certificate.subject}>
						{certificate.subject}
					</span>
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.issuer"/>,
				value:
					<span className="tooltip" data-pr-tooltip={certificate.subject}>
						{certificate.subject}
					</span>
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.algorithm"/>,
				value:
					<span className="tooltip" data-pr-tooltip={certificate.signatureAlgorithm.name}>
						{certificate.signatureAlgorithm.name}
					</span>
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.hash_algorithm"/>,
				value:
					<span className="tooltip" data-pr-tooltip={certificate.signatureAlgorithm.hash.name}>
						{certificate.signatureAlgorithm.hash.name}
					</span>
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.key_length"/>,
				value:
					<span className="tooltip"
						  data-pr-tooltip={certificate.publicKey.algorithm.modulusLength + " " + FileSizeUnit.BIT}>
						{certificate.publicKey.algorithm.modulusLength + " " + FileSizeUnit.BIT}
					</span>
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.before_date.label"/>,
				value:
					<span className={"tooltip"}
						  data-pr-tooltip={
							  certificate.notBefore > currentDate ?
								  Translate.value("control.keystore_viewer.before_date.error") :
								  certificate.notBefore.toLocaleString(i18next.language)
						  }>
						{certificate.notBefore.toLocaleString(i18next.language)}
					</span>,
				className: certificate.notBefore > currentDate ? "error" : ""
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.after_date.label"/>,
				value:
					<span className={"tooltip"}
						  data-pr-tooltip={
							  (certificate.notAfter < currentDate) ?
								  Translate.value("control.keystore_viewer.after_date.error") :
								  certificate.notAfter.toLocaleString(i18next.language)
						  }>
						{certificate.notAfter.toLocaleString(i18next.language)}
					</span>,
				className: certificate.notAfter < currentDate ? "error" : ""
			},
			{
				label: <Translate {...this.props} data="control.keystore_viewer.serial"/>,
				value:
					<span className="tooltip" data-pr-tooltip={certificate.serialNumber}>
						{certificate.serialNumber}
					</span>
			},
		];
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let certificateExpression: RegExp = new RegExp(
			this.CERTIFICATE_START + '[\\s\\S]*?' + this.CERTIFICATE_END, "gm"
		);

		let certificates: RegExpMatchArray = FunctionHelper.applyValue(
			this.props.certificate.certificate!.match(certificateExpression), []
		);
		let x509Certificates: Array<x509.X509Certificate> = [];

		for (let certificate of certificates) {
			try {
				x509Certificates.push(new x509.X509Certificate(certificate));
			} catch (exception: any) {
				// can be ignored
			}
		}

		if (x509Certificates.length === 0) {
			return null;
		}

		let certificateChain: Array<TreeNode> = this.buildCertificateChain(x509Certificates);
		let selected: x509.X509Certificate;
		for (let cert of x509Certificates.reverse()) {
			selected = cert;

			if (cert.subject === this.state.selected) {
				break;
			}
		}

		return (
			<>
				{x509Certificates.length > 1 &&
					<div className="certificate-chain">
						<Tree value={certificateChain} selectionMode="single" selectionKeys={selected!.subject}
							  expandedKeys={this.state.expanded} propagateSelectionDown={false}
							  onSelectionChange={(e) => {
								  this.setState({selected: e.value})
							  }}
							  onToggle={(e) => {
								  this.setState({expanded: e.value})
							  }}/>
					</div>
				}
				<DataTable value={this.buildCertificateValues(selected!)} responsiveLayout={"scroll"}
						   rowClassName={(data) => {
							   let classes: any = {};
							   if (FunctionHelper.exists(data.className)) {
								   classes[data.className!] = true;
							   }
							   return classes;
						   }}>
					<Column field="label"/>
					<Column field="value"/>
				</DataTable>
			</>
		);
	}
}